import React, { useEffect } from "react";

import { faTrash } from "@fortawesome/free-solid-svg-icons";
import { PropTypes } from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components/macro";

import { IntegrationBusinessAction } from "actions";

import { SubtleBadge } from "components/Badge";
import { IconTextButton } from "components/Button";
import { FormCollapse } from "components/Form";
import { Button } from "components/Form/Button";
import { Column, Row } from "components/Layout";
import WaitForSync from "components/LoadingSpinner/WaitForSync";
import Table from "components/Table";

import { IntegrationTypes } from "constants/integrations";
import { ApiModel } from "constants/loading";
import { IntegrationBusinessPermissions } from "constants/permissions";

import { openEditIntegrationBusinessModal } from "lib/navigation";
import { hasPermission } from "lib/permissions";
import { pluralize } from "lib/pluralize";

import { getBusinessById, getIntegrationBusinesses } from "selectors";

const Label = styled.span`
  font-size: 12px;
  font-weight: bold;
  margin-left: 30px;
`;
const ActionsCellRenderer = props => {
  const { id, businessId } = props.value;

  const hasUpdatePermission = hasPermission(
    props.value,
    IntegrationBusinessPermissions.update,
  );

  const dispatch = useDispatch();

  function onClickUnlink() {
    dispatch(IntegrationBusinessAction.unlinkFromBusiness(id, businessId));
  }

  return hasUpdatePermission ? (
    <IconTextButton icon={faTrash} onClick={onClickUnlink} />
  ) : null;
};

const columns = [
  {
    id: "Name",
    Header: "Name",
    accessor: "name",
  },
  {
    id: "type",
    Header: "Type",
    accessor: "type",
  },
  {
    id: "actions",
    Header: "",
    accessor: "",
    Cell: ActionsCellRenderer,
    className: "center",
  },
];

const IntegrationsTable = props => {
  const { businessId, onToggle, isOpen } = props;

  // Load some extra data into our integration business from the (refrehsed) global state - specifically
  // looking at permissions here.
  const allIntegrationBusinesses = useSelector(getIntegrationBusinesses);
  const dispatch = useDispatch();
  useEffect(() => {
    dispatch(IntegrationBusinessAction.requestChanges());
  }, [dispatch]);

  const business = useSelector(getBusinessById(businessId)) || {};
  const { integrationBusinesses = [] } = business;
  const enrichedIntegrationBusinesses = integrationBusinesses.map(
    integrationBusiness => ({
      ...allIntegrationBusinesses?.[integrationBusiness.id],
      ...integrationBusiness,
      businessId,
    }),
  );
  const numIntegrations = integrationBusinesses.length;
  const header = (
    <Row>
      <SubtleBadge>Integrations</SubtleBadge>
      <Row alignCenter flexWrap>
        <Label>
          {numIntegrations} {pluralize("Integration", numIntegrations)}
        </Label>
      </Row>
    </Row>
  );

  return (
    <FormCollapse isOpen={isOpen} onToggle={onToggle} header={header}>
      <Column fullWidth>
        <Button
          onClick={() =>
            openEditIntegrationBusinessModal(IntegrationTypes.Xero, businessId)
          }
        >
          Link To Xero
        </Button>

        <WaitForSync requiredData={[ApiModel.INTEGRATION_BUSINESSES]}>
          <Table data={enrichedIntegrationBusinesses} columns={columns} />
        </WaitForSync>
      </Column>
    </FormCollapse>
  );
};

IntegrationsTable.propTypes = {
  businessId: PropTypes.string,
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func,
};

export default IntegrationsTable;
